import moment from 'moment-timezone'
import { setBrowserCookie } from '@/utils/browser.utils'
import { GUEST_CART_IDS_KEY } from '@/config/cookies.config'

/**
 * Why held app state is no longer usable.
 */
export type StaleReason = 'activeDateElapsed' | 'startTimeElapsed'

type StaleStateArgs = {
  activeDate: Nullable<string>
  activeStartTimeEndTime: Nullable<string>
  timezone?: string
}

/**
 * Constructs a store prefix string based on the provided seller slug or ID, template, and optionally an event name or ID.
 *
 * @param {string} sellerSlugOrId - The unique seller identifier, either a slug or ID.
 * @param {string} template - The template string used for the store prefix.
 * @param {string} [maybeEventNameOrId] - An optional event name or ID to include in the constructed prefix.
 * @returns {string} A formatted string representing the store prefix.
 */
export const createStorePrefix = (sellerSlugOrId: string, template: string, maybeEventNameOrId?: string): string => (
  [sellerSlugOrId, template, maybeEventNameOrId]
    .filter(Boolean)
    .join('/')
)

/**
 * Determines whether the selected date or timeslot has elapsed.
 *
 * Shared by the persist rehydration check and the tab-focus check so a tab left
 * open across a date boundary is treated the same as a reload.
 *
 * @param {Object} params - The parameters object.
 * @param {Nullable<string>} params.activeDate - The currently selected date.
 * @param {Nullable<string>} params.activeStartTimeEndTime - End of the selected timeslot.
 * @param {string | undefined} params.timezone - The seller timezone, when known.
 * @returns {Nullable<StaleReason>} Why the state is stale, or null when it is still usable.
 */
export const getStaleStateReason = ({
  activeDate,
  activeStartTimeEndTime,
  timezone,
}: StaleStateArgs): Nullable<StaleReason> => {
  const maybeTimezonedNow = timezone
    ? moment.tz(moment(), timezone)
    : moment()

  // stale if the active date is before today
  if (activeDate) {
    const maybeTimezonedActiveDate = timezone
      ? moment.tz(activeDate, timezone)
      : moment(activeDate)

    if (maybeTimezonedActiveDate.isBefore(maybeTimezonedNow, 'day')) {
      return 'activeDateElapsed'
    }
  }

  // stale if the selected timeslot has already ended
  if (activeStartTimeEndTime) {
    const maybeTimezonedActiveStartTime = timezone
      ? moment.tz(activeStartTimeEndTime, timezone)
      : moment(activeStartTimeEndTime)

    if (maybeTimezonedActiveStartTime.isBefore(maybeTimezonedNow, 'minute')) {
      return 'startTimeElapsed'
    }
  }

  return null
}

/**
 * Generates the next set of form values based on the input parameters.
 *
 * @param {Object} params - The parameters object.
 * @param {FormValuesT} params.formValues - The current set of form values.
 * @param {LoggedInUserProfile | undefined} params.loggedInUser - The profile of the logged-in user, if available.
 * @param {boolean} params.isLocal - A flag to indicate if the operation is localized.
 * @returns {FormValuesT} The updated form values based on the provided inputs.
 */
export const getNextFormValues = ({
  formValues,
  loggedInUser,
  isLocal,
}: {
  formValues: FormValuesT
  loggedInUser: LoggedInUserProfile | undefined
  isLocal: boolean
}): FormValuesT => {
  let nextFormValues = { ...formValues }

  // set from profile
  if (loggedInUser) {
    const {
      firstName,
      lastName,
      email,
      phoneNumber,
      zipCode,
    } = loggedInUser

    nextFormValues = {
      ...nextFormValues,
      email,
      emailConfirm: email,
      firstName,
      lastName,
      zipCode,
      phoneNumber,
      country: formValues.country,
    }
  }

  if (isLocal) {
    nextFormValues = {
      ...nextFormValues,
      zipCode: formValues.zipCode || '',
    }
  }
  return nextFormValues
}

/**
 * Make sure that the keys in the resetKeys array are not present in the partial object.
 * @param partial
 * @param resetKeys
 */
export const validateKeysAreUnique = <T>(partial: Partial<T>, resetKeys: Array<keyof T>): void => {
  const overlappingKeys = resetKeys.filter((key) => key in partial)
  if (overlappingKeys.length > 0) {
    throw new Error(`Cannot reset and update the same key(s): ${overlappingKeys.join(', ')}`)
  }
}

/**
 * Determines whether a given key is a valid key of the specified object and that the value corresponding
 * to the key in the object is not undefined.
 *
 * @template T - The type of the object.
 * @param {keyof T} key - The key to validate.
 * @param {T} obj - The object to check the key against.
 * @returns {boolean} - Returns true if the key exists in the object and its value is not undefined, otherwise false.
 */
export const isValidKey = <T extends object>(key: keyof T, obj: T): key is keyof T => (
  key in obj && typeof obj[key] !== 'undefined'
)

/**
 * Removes the guest cart ID from the browser cookies by setting the corresponding
 * cookie's value to an empty string and setting its expiration time to a past date.
 *
 * The function modifies the cookie identified by the key specified as `GUEST_CART_IDS_KEY`.
 * This effectively invalidates the guest cart ID and ensures it is no longer stored in the
 * user's browser.
 *
 * This function does not accept or return any parameters or values.
 */
export const removeGuestCartId = (): void => {
  setBrowserCookie(GUEST_CART_IDS_KEY, '', { minutesToExpire: -1 })
}
